import type { RpcMessage } from './protocol';

/** A postMessage-like transport. The worker side wraps `self`; the host side
 *  wraps the Worker. Kept abstract so the peer is testable over an in-memory
 *  pair with no real Worker. */
export interface RpcChannel {
  post(msg: RpcMessage): void;
  onMessage(cb: (msg: RpcMessage) => void): () => void;
}

export type MethodHandler = (params: unknown) => unknown | Promise<unknown>;
export type EventListener = (payload: unknown) => void;

/**
 * A symmetric request/response + events peer. This is the only channel in and
 * out of the sealed plugin room: the worker reaches the host through it, and
 * the host reaches the worker through it. Either side can register method
 * handlers (`handle`), call the other (`call`), and exchange one-way events
 * (`emit` / `on`).
 */
export class RpcPeer {
  private nextId = 1;
  private readonly pending = new Map<
    number,
    { resolve: (v: unknown) => void; reject: (e: Error) => void }
  >();
  private readonly methods = new Map<string, MethodHandler>();
  private readonly events = new Map<string, Set<EventListener>>();
  private readonly off: () => void;
  private disposed = false;

  constructor(private readonly channel: RpcChannel) {
    this.off = channel.onMessage((msg) => this.receive(msg));
  }

  /** Register a handler the remote peer can `call`. */
  handle(method: string, handler: MethodHandler): void {
    this.methods.set(method, handler);
  }

  /** Call a method on the remote peer; resolves with its result or rejects. */
  call(method: string, params?: unknown): Promise<unknown> {
    if (this.disposed) return Promise.reject(new Error('rpc peer disposed'));
    const id = this.nextId++;
    return new Promise<unknown>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.channel.post({ t: 'req', id, method, params });
    });
  }

  /** Fire a one-way event to the remote peer's subscribers. */
  emit(event: string, payload?: unknown): void {
    if (this.disposed) return;
    this.channel.post({ t: 'evt', event, payload });
  }

  /** Subscribe to a remote event; returns an unsubscribe function. */
  on(event: string, cb: EventListener): () => void {
    let set = this.events.get(event);
    if (!set) {
      set = new Set();
      this.events.set(event, set);
    }
    const s = set;
    s.add(cb);
    return () => {
      s.delete(cb);
    };
  }

  /** Tear down: stop listening, reject all pending calls. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.off();
    const err = new Error('rpc peer disposed');
    for (const slot of this.pending.values()) slot.reject(err);
    this.pending.clear();
    this.methods.clear();
    this.events.clear();
  }

  private receive(msg: RpcMessage): void {
    if (this.disposed) return;
    switch (msg.t) {
      case 'req':
        void this.dispatch(msg);
        break;
      case 'res': {
        const slot = this.pending.get(msg.id);
        if (!slot) return;
        this.pending.delete(msg.id);
        if (msg.ok) slot.resolve(msg.result);
        else slot.reject(new Error(msg.error));
        break;
      }
      case 'evt': {
        const set = this.events.get(msg.event);
        if (set) for (const cb of [...set]) cb(msg.payload);
        break;
      }
    }
  }

  private async dispatch(msg: {
    t: 'req';
    id: number;
    method: string;
    params: unknown;
  }): Promise<void> {
    const handler = this.methods.get(msg.method);
    if (!handler) {
      this.channel.post({
        t: 'res',
        id: msg.id,
        ok: false,
        error: `no handler for method "${msg.method}"`,
      });
      return;
    }
    try {
      const result = await handler(msg.params);
      if (!this.disposed) this.channel.post({ t: 'res', id: msg.id, ok: true, result });
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      if (!this.disposed) this.channel.post({ t: 'res', id: msg.id, ok: false, error });
    }
  }
}
